import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, Play, Trophy } from "lucide-react";

import { sportsService, type Competition, type Match, type Sport } from "../../services/sportsService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ReasonPanel, errorLine } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { COMPETITION_TYPES, competitionConfig, competitionStatus, day, isOpenMatch, labelFrom, matchStatus, scoreLine, standings } from "./sportsLogic";

function CompetitionForm({ sports, onClose, onDone }: { sports: Sport[]; onClose: () => void; onDone: (row: Competition) => void }) {
  const [start, setStart] = useState(todayIso());
  const save = useMutation({ mutationFn: sportsService.createCompetition, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const text = (name: string) => String(data.get(name) ?? "").trim() || null;
    save.mutate({
      name: text("name"),
      sport: Number(text("sport")),
      competition_type: text("competition_type"),
      start_date: start,
      end_date: text("end_date"),
      venue: text("venue"),
      organizer: text("organizer"),
      contact_person: text("contact_person"),
      contact_phone: text("contact_phone"),
      prizes: text("prizes"),
      description: text("description"),
    });
  };
  return (
    <FormModal
      id="competition"
      title="New competition"
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The competition could not be created.") : undefined}
      saveLabel="Create it"
      note="Schedule its matches under Fixtures."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The competition" divided={false}>
        <Field label="Name" className="sm:col-span-2" hint="e.g. Inter-house football cup 2026, Kampala schools athletics.">
          <input required name="name" maxLength={200} className="input mt-1" />
        </Field>
        <Field label="Sport">
          <select required name="sport" className="input mt-1">
            <option value="">Choose a sport</option>
            {sports.filter((row) => row.is_active).map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </Field>
        <Field label="Kind">
          <select name="competition_type" defaultValue="tournament" className="input mt-1">
            {COMPETITION_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Starts">
          <input required type="date" value={start} onChange={(event) => setStart(event.target.value)} className="input mt-1" />
        </Field>
        <Field label="Ends">
          <input required type="date" name="end_date" min={start} defaultValue={start} className="input mt-1" />
        </Field>
        <Field label="Venue" optional>
          <input name="venue" maxLength={200} className="input mt-1" />
        </Field>
        <Field label="Organised by" optional hint="e.g. the school, USSSA, a district association.">
          <input name="organizer" maxLength={200} className="input mt-1" />
        </Field>
        <Field label="Contact person" optional>
          <input name="contact_person" maxLength={100} className="input mt-1" />
        </Field>
        <Field label="Contact phone" optional>
          <input type="tel" name="contact_phone" maxLength={20} className="input mt-1" />
        </Field>
        <Field label="Prizes" optional className="sm:col-span-2">
          <input name="prizes" className="input mt-1" />
        </Field>
        <Field label="Description" optional className="sm:col-span-2">
          <textarea name="description" rows={2} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Tournaments, leagues and friendlies, their matches and the table. */
export function CompetitionsPanel({ competitions, matches, sports, isLoading, isError, onRetry, canManage, onChanged }: {
  competitions?: Competition[];
  matches: Match[];
  sports: Sport[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  onChanged: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const done = (text: string, close = false) => {
    setNotice({ tone: "success", text });
    if (close) setViewing(null);
    onChanged();
  };
  const start = useMutation({ mutationFn: sportsService.startCompetition, onSuccess: (row) => done(`${row.name} is under way; its matches can kick off.`) });
  const finish = useMutation({ mutationFn: sportsService.completeCompetition, onSuccess: (row) => done(`${row.name} is finished.`, true) });
  const cancel = useMutation({ mutationFn: sportsService.cancelCompetition, onSuccess: (row) => done(`${row.name} was cancelled.`, true) });
  const matchesIn = (id: number) => matches.filter((row) => row.competition === id);

  const columns: RegisterColumn<Competition>[] = [
    { header: "Competition", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name, className: "min-w-[12rem]" },
    { header: "Sport", cell: (row) => row.sport_name, exportValue: (row) => row.sport_name, className: "text-gray-700" },
    { header: "Kind", cell: (row) => labelFrom(COMPETITION_TYPES, row.competition_type), exportValue: (row) => labelFrom(COMPETITION_TYPES, row.competition_type), className: "text-gray-700" },
    { header: "Dates", cell: (row) => `${day(row.start_date)} – ${day(row.end_date)}`, exportValue: (row) => `${day(row.start_date)} to ${day(row.end_date)}`, className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Matches", cell: (row) => { const all = matchesIn(row.id); return `${all.filter((item) => item.status === "completed").length} of ${all.filter((item) => item.status !== "cancelled").length} played`; }, exportValue: (row) => matchesIn(row.id).length, className: "text-gray-700" },
    { header: "Venue", cell: (row) => row.venue || "—", exportValue: (row) => row.venue ?? "", className: "text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot {...competitionStatus(row, today)} />, exportValue: (row) => competitionStatus(row, today).label },
  ];

  const competition = viewing !== null ? competitions?.find((row) => row.id === viewing) : undefined;
  const fixtures = competition ? matchesIn(competition.id).sort((a, b) => `${a.match_date}${a.match_time}`.localeCompare(`${b.match_date}${b.match_time}`)) : [];
  const table = standings(fixtures);
  const openFixtures = fixtures.filter(isOpenMatch).length;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Competitions"
        description={canManage ? "Open a competition to start it, see its table and close it." : "Tournaments, leagues and friendlies."}
        actions={canManage && (
          <button type="button" disabled={!sports.some((row) => row.is_active)} onClick={() => setAdding(true)} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
            <Trophy className="mr-1.5 h-3.5 w-3.5" /> New competition
          </button>
        )}
        rows={competitions}
        config={competitionConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          [start, finish, cancel].forEach((mutation) => mutation.reset());
          setViewing(row.id);
        }}
        noun={["competition", "competitions"]}
        exportName={{ file: "competitions", title: "Sports Competitions" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "No competitions yet." : "No competitions to show."}
      />

      {competition && (
        <DetailsModal
          size="3xl"
          title={competition.name}
          headline={{ label: labelFrom(COMPETITION_TYPES, competition.competition_type), value: `${day(competition.start_date)} – ${day(competition.end_date)}`, note: `${competition.sport_name}${competition.venue ? ` · ${competition.venue}` : ""}` }}
          status={competitionStatus(competition, today)}
          facts={[
            ["Organised by", competition.organizer ?? undefined],
            ["Contact", [competition.contact_person, competition.contact_phone].filter(Boolean).join(", ") || undefined],
            ["Matches", `${fixtures.filter((row) => row.status === "completed").length} played, ${openFixtures} still to play`],
            ["Prizes", competition.prizes ?? undefined],
          ]}
          sections={[
            { title: "About", body: competition.description },
            { title: "Why it was cancelled", body: competition.cancellation_reason, tone: "danger" },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && competition.status === "scheduled" && (
                <button type="button" disabled={start.isPending} onClick={() => start.mutate(competition.id)} className="btn btn-primary gap-1.5">
                  <Play className="h-4 w-4" /> Start it
                </button>
              )}
              {canManage && competition.status === "in_progress" && (
                <button type="button" disabled={finish.isPending || openFixtures > 0} title={openFixtures ? "Finish or cancel every open match first." : undefined} onClick={() => finish.mutate(competition.id)} className="btn btn-primary gap-1.5">
                  <CheckCircle2 className="h-4 w-4" /> Finish it
                </button>
              )}
            </>
          }
        >
          {canManage && competition.status === "in_progress" && openFixtures > 0 && (
            <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">{openFixtures} {openFixtures === 1 ? "match is" : "matches are"} still to play or record; it can be finished once they are done or cancelled.</p>
          )}
          {[start, finish].map((mutation, index) => mutation.isError && <div key={index}>{errorLine(mutation.error, "That could not be done.")}</div>)}

          {table.length > 0 && (
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Table</h4>
              <div className="sheet-wrap mt-1.5">
                <table className="sheet">
                  <thead>
                    <tr>
                      <th className="sheet-rownum">#</th>
                      <th>Team</th>
                      <th className="text-right">P</th>
                      <th className="text-right">W</th>
                      <th className="text-right">D</th>
                      <th className="text-right">L</th>
                      <th className="text-right">GF</th>
                      <th className="text-right">GA</th>
                      <th className="text-right">Pts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {table.map((row, index) => (
                      <tr key={row.team}>
                        <td className="sheet-rownum">{index + 1}</td>
                        <td className="font-medium text-gray-900">{row.name}</td>
                        {[row.played, row.won, row.drawn, row.lost, row.scored, row.conceded].map((value, at) => <td key={at} className="text-right tabular-nums">{value}</td>)}
                        <td className="text-right font-semibold tabular-nums">{row.points}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-1 text-xs text-gray-500">Three points for a win, one for a draw.</p>
            </section>
          )}

          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Matches ({fixtures.length})</h4>
            {fixtures.length ? (
              <ul className="mt-1.5 divide-y divide-gray-100 rounded-lg border border-gray-200">
                {fixtures.map((row) => (
                  <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                    <span>
                      <span className="tabular-nums text-gray-500">{day(row.match_date)}</span>
                      <span className="ml-2 font-medium text-gray-900">{scoreLine(row)}</span>
                    </span>
                    <StatusDot {...matchStatus(row, today)} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1.5 text-sm text-gray-500">No matches scheduled; add them under Fixtures.</p>
            )}
          </section>

          {canManage && (competition.status === "scheduled" || competition.status === "in_progress") && (
            <ReasonPanel
              title="Cancel the competition"
              description="Matches already played keep their results."
              placeholder="e.g. The organisers called it off for the term."
              context={{ key: "sports.note", params: { kind: "competition_cancel", id: competition.id } }}
              button="Cancel the competition"
              danger
              busy={cancel.isPending}
              error={cancel.error}
              errorText="The competition could not be cancelled."
              onSubmit={(reason) => cancel.mutate({ id: competition.id, reason })}
            />
          )}
        </DetailsModal>
      )}

      {adding && (
        <CompetitionForm
          sports={sports}
          onClose={() => setAdding(false)}
          onDone={(saved) => {
            setAdding(false);
            done(`${saved.name} created. Schedule its matches under Fixtures.`);
            setViewing(saved.id);
          }}
        />
      )}
    </div>
  );
}
